// Build a preview: /growth/leads/:id/preview. Steps: read their site (or paste
// or upload the data), check what the server read, save. The check is the same
// pass as the save with no writes, so what it says is what gets built.
//
// Reading their site starts on its own when the page opens and nothing was read
// yet (owner, November 3: "attempt to pull and scrape data from their existing
// site"): a RecLeague league's whole latest year, or any other site's schedule,
// results and standings pages. What it finds fills Games and Teams below, as a
// table, so it can be checked and changed before Check.
//
// Team-level facts only. lib/previewImport.ts leaves every other column in the
// browser, and the server would drop it anyway.

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { LeadPreviewDraft } from '@/api/growthInsights'
import type { PreviewCreateBody, PreviewReport } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  FormField,
  InlineAlert,
  KeyValueList,
  LoadingState,
  SelectInput,
  Textarea,
  UploadIcon,
  useToast,
} from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { parseGames, parseTeams } from '@/lib/previewImport'
import { daysFrom } from '@/lib/previews'
import { invalidateQueries, useQuery } from '@/lib/query'
import { usePoll } from '@/lib/useFinder'
import { LifeField } from './previewShared'

const TEMPLATES = [
  { value: 'classic', label: 'Classic' },
  { value: 'arena', label: 'Arena' },
  { value: 'huddle', label: 'Huddle' },
]
const SPORTS = [
  { value: 'basketball', label: 'Basketball' },
  { value: 'soccer', label: 'Soccer' },
]
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
]

const SHOWN_GAMES = 200

function when(text: string | null): string {
  return text ? text.replace('T', ' ').slice(0, 16) : ''
}

function Report({ report }: { report: PreviewReport }) {
  return (
    <>
      <Card>
        <CardHeader title="What this preview will show" />
        <KeyValueList
          items={[
            { label: 'Teams', value: report.summary.teams },
            { label: 'Scheduled games', value: report.summary.scheduled },
            { label: 'Results', value: report.summary.completed },
            { label: 'Rows skipped', value: report.summary.problems },
          ]}
        />
      </Card>
      {report.problems.length > 0 ? (
        <Card>
          <CardHeader title="Rows that were skipped" description="The rest of the data goes on without them." />
          <ul aria-label="Skipped rows" className="grid gap-1.5 text-[13px] text-ink">
            {report.problems.map((p, i) => (
              <li key={i}>
                {p.kind === 'team' ? 'Team' : 'Game'} row {p.row}: {p.message}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Card>
        <CardHeader title="Teams" />
        <ul aria-label="Teams" className="flex flex-wrap gap-1.5">
          {report.teams.map((t) => (
            <li key={t.short_code}>
              <Badge tone="neutral">{t.name}</Badge>
            </li>
          ))}
        </ul>
      </Card>
      {report.games.length > 0 ? (
        <Card flush className="overflow-hidden">
          <div className="p-4 pb-0">
            <CardHeader title="Games" description={report.games.length > SHOWN_GAMES ? `The first ${SHOWN_GAMES} of ${report.games.length}.` : undefined} />
          </div>
          <div className="overflow-x-auto">
            <table aria-label="Games" className="w-full min-w-[32rem] text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-xs text-muted">
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Home</th>
                  <th className="px-4 py-2 font-medium">Away</th>
                  <th className="px-4 py-2 font-medium">Score</th>
                </tr>
              </thead>
              <tbody>
                {report.games.slice(0, SHOWN_GAMES).map((g) => (
                  <tr key={g.row} className="border-b border-line last:border-0">
                    <td className="whitespace-nowrap px-4 py-2 text-muted">{when(g.when)}</td>
                    <td className="px-4 py-2 text-ink">{g.home_team}</td>
                    <td className="px-4 py-2 text-ink">{g.away_team}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-ink">{g.status === 'completed' ? `${g.home_score}–${g.away_score}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </>
  )
}

const csvCell = (v: string | number | null | undefined) => {
  const s = v === null || v === undefined ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** What a read found, as the Games and Teams boxes take it: a table, and the teams no game names. */
function draftText(rows: NonNullable<LeadPreviewDraft['rows']>): { games: string; teams: string } {
  const games = rows.games.length === 0 ? '' : [
    'Date,Time,Home,Away,Home score,Away score,Round',
    ...rows.games.map((g) => [g.game_date, g.game_time ?? '', g.home_team, g.away_team, g.home_score, g.away_score, g.round_number].map(csvCell).join(',')),
  ].join('\n')
  const named = new Set(rows.games.flatMap((g) => [g.home_team.toLowerCase(), g.away_team.toLowerCase()]))
  const teams = rows.teams.map((t) => t.name).filter((n) => !named.has(n.toLowerCase())).join('\n')
  return { games, teams }
}

const hostOf = (url: string | null | undefined) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : null
  } catch {
    return null
  }
}

/**
 * Their site: reads it on its own the first time, says what it found, and hands
 * it to the boxes below. Read again replaces what's in them.
 */
function FromTheirSite({ leadId, empty, onFound }: { leadId: number; empty: boolean; onFound: (text: { games: string; teams: string }) => void }) {
  const { data, error, status, refetch } = useQuery(['growth', 'lead-preview-draft', leadId], (signal) => api.growth.leads.previewDraft(leadId, signal))
  const [problem, setProblem] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)
  /** The page has looked at whether to read on its own. */
  const started = useRef(false)
  /** A read was asked for here, so what it finds replaces what's in the boxes. */
  const asked = useRef(false)
  /** The draft last seen: undefined before the first answer. */
  const seen = useRef<string | null | undefined>(undefined)
  const run = data?.run ?? null
  const running = run?.status === 'running'
  usePoll(running, refetch)

  const read = async () => {
    setProblem(null)
    setStarting(true)
    try {
      await api.growth.leads.readSite(leadId)
      asked.current = true
      refetch()
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setStarting(false)
    }
  }

  // The first time, it reads without being asked.
  useEffect(() => {
    if (!data || started.current) return
    started.current = true
    if (data.urls.length > 0 && !data.draft && !data.run) void read()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // What a read finds goes into the boxes: after a read from here, or on arrival when they're empty.
  // A read that found nothing leaves them as they are.
  useEffect(() => {
    if (!data || running) return
    const stamp = data.draft ? `${data.draft.id}:${data.draft.updated_at}` : null
    if (seen.current === stamp) return
    const firstLook = seen.current === undefined
    seen.current = stamp
    if (!data.rows || (data.rows.games.length === 0 && data.rows.teams.length === 0)) return
    if (asked.current || (firstLook && empty)) {
      asked.current = false
      onFound(draftText(data.rows))
    }
  }, [data, running, empty, onFound])

  const stop = async () => {
    if (!run) return
    setStopping(true)
    try {
      await api.growth.finder.stopRun(run.id)
      refetch()
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setStopping(false)
    }
  }

  if (status === 'loading' && !data) return null
  if (!data) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't check their site" onRetry={refetch} />
      </Card>
    )
  }

  const runUrl = typeof run?.params?.url === 'string' ? run.params.url : null
  const where = (running ? hostOf(runUrl) : null) ?? hostOf(data.draft?.source_url) ?? hostOf(data.urls[0])
  const teams = data.rows?.teams.length ?? 0
  const games = data.rows?.games.length ?? 0

  return (
    <Card>
      <CardHeader
        title="From their site"
        description="Team names, the schedule and scores, read from their public pages. Never players."
        actions={
          data.urls.length > 0 && !running ? (
            <Button size="sm" loading={starting} onClick={() => void read()}>
              {data.draft ? 'Read again' : 'Read their site'}
            </Button>
          ) : running ? (
            <Button size="sm" loading={stopping} onClick={() => void stop()}>
              Stop
            </Button>
          ) : undefined
        }
      />
      <div className="grid gap-2 text-[13px]">
        {data.urls.length === 0 ? (
          <p className="text-muted">This lead has no website or listing to read. Paste their schedule below.</p>
        ) : running || starting ? (
          <p className="text-ink" role="status">
            Reading {where}… {run ? `${run.pages_fetched} ${run.pages_fetched === 1 ? 'page' : 'pages'} so far.` : ''}
          </p>
        ) : data.draft && (teams > 0 || games > 0) ? (
          <p className="text-ink" role="status">
            Found <strong>{teams} {teams === 1 ? 'team' : 'teams'}</strong> and <strong>{games} {games === 1 ? 'game' : 'games'}</strong> on {hostOf(data.draft.source_url)}. They're in the boxes below: check them, then press Check.
          </p>
        ) : run && run.status !== 'running' ? (
          <p className="text-muted" role="status">
            {run.note ?? 'Nothing found.'} Paste their schedule below instead.
          </p>
        ) : null}
        {data.left_out > 0 && !running ? (
          <p className="text-muted">{data.left_out} {data.left_out === 1 ? 'game has' : 'games have'} no date we could read, so {data.left_out === 1 ? "it's" : "they're"} left out.</p>
        ) : null}
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      </div>
    </Card>
  )
}

export default function PreviewCreatePage() {
  const leadId = Number(useParams().id)
  useDocumentTitle('Build a preview')
  const navigate = useNavigate()
  const toast = useToast()
  const { data: lead, error, status, refetch } = useQuery(['growth', 'lead', leadId], (signal) => api.growth.leads.get(leadId, signal))

  const [teamsText, setTeamsText] = useState('')
  const [gamesText, setGamesText] = useState('')
  const [choice, setChoice] = useState('30')
  const [custom, setCustom] = useState('')
  const [template, setTemplate] = useState('classic')
  const [sport, setSport] = useState<string | null>(null)
  const [timezone, setTimezone] = useState('America/Chicago')
  const [report, setReport] = useState<PreviewReport | null>(null)
  const [pending, setPending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const fill = useCallback((text: { games: string; teams: string }) => {
    setGamesText(text.games)
    setTeamsText(text.teams)
    setReport(null)
  }, [])

  const teams = useMemo(() => parseTeams(teamsText), [teamsText])
  const games = useMemo(() => parseGames(gamesText), [gamesText])
  const leftOut = [...new Set([...teams.leftOut, ...games.leftOut])]
  const days = daysFrom(choice, custom)
  const chosenSport = sport ?? (lead?.sport === 'soccer' ? 'soccer' : 'basketball')
  const hasData = teams.rows.length > 0 || games.rows.length > 0
  const ready = hasData && games.missing.length === 0 && days !== null

  if (status === 'loading' && !lead) return <LoadingState label="Loading lead…" />
  if (!lead) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this lead" onRetry={refetch} />
      </Card>
    )
  }

  const body = (): PreviewCreateBody => ({ lead_id: leadId, teams: teams.rows, games: games.rows, days: days!, template, sport: chosenSport, timezone })

  const check = async () => {
    setPending(true)
    setProblem(null)
    try {
      setReport(await api.growth.previews.check(body()))
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  const save = async () => {
    setPending(true)
    setProblem(null)
    try {
      const { preview, provisioning } = await api.growth.previews.create(body())
      invalidateQueries('growth')
      // Built either way; its address on Cloudflare can fail on its own, as for a new tenant.
      if (provisioning?.subdomain_dns === 'failed') toast.error(provisioning.message ?? "The preview was built, but its address couldn't be set up on Cloudflare.")
      else toast.success('The preview is live at its own address.')
      navigate(`/growth/previews/${preview.id}`)
    } catch (err) {
      setProblem(isApiError(err) && err.code === 'preview_exists' ? 'This lead already has a preview.' : errorMessage(err))
      setPending(false)
    }
  }

  const onFile = async (e: ChangeEvent<HTMLInputElement>, set: (text: string) => void) => {
    const file = e.target.files?.[0]
    if (file) set(await file.text())
    e.target.value = ''
  }

  const crumbs = [
    { label: 'Leads', to: '/growth/leads' },
    { label: lead.name, to: `/growth/leads/${leadId}` },
    { label: 'Build a preview' },
  ]

  if (report) {
    return (
      <div className="grid grid-cols-1 gap-5">
        <PageHeader crumbs={crumbs} title="Check the preview" subtitle={lead.name} />
        <Report report={report} />
        {report.dropped_columns.length > 0 ? (
          <InlineAlert title="Left out">{report.dropped_columns.join(', ')}. A preview has no place for these.</InlineAlert>
        ) : null}
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={() => setReport(null)} disabled={pending}>
            Back
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void save()}>
            Build the preview
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader crumbs={crumbs} title="Build a preview" subtitle={lead.name} description="A private ScoreLM league for them, on its own address. They sign in with the link in your email and can change anything, as a customer would." />

      <FromTheirSite leadId={leadId} empty={!gamesText.trim() && !teamsText.trim()} onFound={fill} />

      <InlineAlert title="Team-level facts only">
        League name, team names, schedule and scores. No player names, rosters, photos, emails or phone numbers, for any league. Their logo stays out too: they can add their own once they sign in.
      </InlineAlert>

      <Card>
        <CardHeader title="Games" description="Paste a table or upload a CSV. The first line names the columns: Home, Away, Date, and any of Time, Home score, Away score, Score, Round. Other columns stay here and are never sent." />
        <FormField label="Games">{({ id }) => <Textarea id={id} rows={8} className="font-mono text-[12.5px]" placeholder={'Date,Time,Home,Away,Score\n9/5/2026,6:30 PM,Hawks,Bulls,61-55'} value={gamesText} onChange={(e) => setGamesText(e.target.value)} />}</FormField>
        <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-ink">
          <UploadIcon size={15} className="text-subtle" />
          Upload a CSV
          <input type="file" accept=".csv,text/csv,text/plain" aria-label="Games CSV" className="sr-only" onChange={(e) => void onFile(e, setGamesText)} />
        </label>
        {games.missing.length > 0 ? <div className="mt-2"><InlineAlert tone="warn">No column for: {games.missing.join(', ')}.</InlineAlert></div> : null}
      </Card>

      <Card>
        <CardHeader title="Teams" description="One per line. Only needed for a team with no games yet: the games name the rest." />
        <FormField label="Teams">{({ id }) => <Textarea id={id} rows={4} value={teamsText} onChange={(e) => setTeamsText(e.target.value)} />}</FormField>
      </Card>

      {leftOut.length > 0 ? <InlineAlert title="Left out">{leftOut.join(', ')}. A preview has no place for these, so they stay on your screen.</InlineAlert> : null}

      <Card>
        <CardHeader title="Site" />
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="Template">{({ id }) => <SelectInput id={id} value={template} options={TEMPLATES} onChange={(e) => setTemplate(e.target.value)} />}</FormField>
          <FormField label="Sport">{({ id }) => <SelectInput id={id} value={chosenSport} options={SPORTS} onChange={(e) => setSport(e.target.value)} />}</FormField>
          <FormField label="Time zone">{({ id }) => <SelectInput id={id} value={timezone} options={TIMEZONES} onChange={(e) => setTimezone(e.target.value)} />}</FormField>
        </div>
        <div className="mt-4">
          <LifeField label="Deletes itself after" choice={choice} custom={custom} onChoice={setChoice} onCustom={setCustom} />
        </div>
      </Card>

      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to={`/growth/leads/${leadId}`} className="inline-flex h-9 items-center justify-center rounded-md px-3 text-[13px] font-semibold text-muted hover:text-ink">
          Cancel
        </Link>
        <Button variant="primary" disabled={!ready} loading={pending} onClick={() => void check()}>
          Check
        </Button>
      </div>
    </div>
  )
}
